/**
 * Translation-layer module — bridges praxis core's canonical snake_case Bead
 * shape (on-disk YAML) to clawcraft's camelCase `BeadValueObject` (Convex
 * columns + frontend props). Praxis is the single source of TYPE truth
 * (`BeadType`, `BeadState` are imported type-only); runtime enum tuples
 * are declared locally because praxis's `dist/core.mjs` transitively
 * pulls in `rolldown-runtime-*.mjs` which has a (dead, unused) `import
 * { createRequire } from "node:module"` that Convex's V8-isolate
 * bundler cannot resolve. Bidirectional type-completeness assertions
 * below make tsc fail compile if praxis adds/removes a type or state
 * and the local tuples don't follow suit — preserving the single-type-
 * source contract without paying the Convex-bundle cost of importing
 * praxis runtime. See praxis-doctrine §5.2 v1.8.1 for the constraint;
 * see bead `rnk-fo9z` (or successor) for the upstream praxis-build fix
 * that would let runtime tuples re-import from praxis directly.
 *
 * The value-object shape is defined locally because clawcraft's
 * `praxisId` field deliberately diverges from praxis's `id` to avoid
 * Convex's reserved `_id` column. The snake→camel translation happens
 * producer-side in praxis CLI's projection emitter (see praxis-doctrine
 * §5.2). clawcraft consumes camelCase end-to-end; no runtime translator
 * function lives here (would be §8 dormant scaffolding).
 *
 * Pure module: no Convex, no React, no external libs at runtime. The
 * praxis import is type-only (erased at compile time, zero bundle cost
 * in any consumer). All exported functions take a single `input`
 * object and destructure.
 */

import type {
  BeadType as PraxisBeadType,
  BeadState as PraxisBeadState,
  BeadWriter as PraxisBeadWriter,
  BeadRole as PraxisBeadRole,
} from "@soulbound-labs/praxis";

// Local runtime tuples — single-sourced for ALL clawcraft consumers
// (frontend, Convex schema, projection). The bidirectional type-
// completeness assertions below pin these to praxis's authoritative
// types at compile time.
export const BEAD_TYPES = [
  "inbox",
  "next-action",
  "project",
  "waiting-for",
  "someday",
  "reference",
  "decision",
  "commitment",
  "output",
] as const;

export type BeadType = (typeof BEAD_TYPES)[number];

export const BEAD_STATES = [
  "inbox",
  "clarified",
  "active",
  "waiting",
  "dormant",
  "done",
  "archived",
] as const;

export type BeadState = (typeof BEAD_STATES)[number];

export const BEAD_SCHEMA_VERSION = "praxis.bead/v1" as const;

// ─── Bidirectional type parity with praxis ────────────────────────
// Two-way compile-time exhaustiveness. If praxis adds a new type/state
// and clawcraft misses it, `_AllPraxis*Covered` fails. If clawcraft
// has a phantom value praxis doesn't have, `_NoExtraLocal*` fails.
// Either direction: tsc errors at next clawcraft typecheck.
type _AllPraxisTypesCovered = PraxisBeadType extends BeadType ? true : never;
type _NoExtraLocalTypes = BeadType extends PraxisBeadType ? true : never;
const _typeCheckA: _AllPraxisTypesCovered = true;
void _typeCheckA;
const _typeCheckB: _NoExtraLocalTypes = true;
void _typeCheckB;

type _AllPraxisStatesCovered = PraxisBeadState extends BeadState ? true : never;
type _NoExtraLocalStates = BeadState extends PraxisBeadState ? true : never;
const _stateCheckA: _AllPraxisStatesCovered = true;
void _stateCheckA;
const _stateCheckB: _NoExtraLocalStates = true;
void _stateCheckB;

// ─── Bead writer + role (two-writer rule) ────────────────────────────
// `writer` distinguishes the workspace projection from the validation
// pipeline as the producer of a bead row. praxis-doctrine §5.2 names
// these two as the only authorized writers of `beads`. `role` is the
// orthogonal "what the bead represents" axis; v1 has a single literal,
// `prior_work`, emitted only by the validation pipeline.
//
// Runtime tuples are declared locally per the Convex-bundling constraint
// (CLAUDE.md); type-completeness assertions pin them to praxis.

export const BEAD_WRITERS = ["workspace", "validation"] as const;
export type BeadWriter = (typeof BEAD_WRITERS)[number];

export const BEAD_ROLES = ["prior_work", "execution"] as const;
export type BeadRole = (typeof BEAD_ROLES)[number];

type _AllPraxisWritersCovered = PraxisBeadWriter extends BeadWriter
  ? true
  : never;
type _NoExtraLocalWriters = BeadWriter extends PraxisBeadWriter ? true : never;
const _writerCheckA: _AllPraxisWritersCovered = true;
void _writerCheckA;
const _writerCheckB: _NoExtraLocalWriters = true;
void _writerCheckB;

type _AllPraxisRolesCovered = PraxisBeadRole extends BeadRole ? true : never;
type _NoExtraLocalRoles = BeadRole extends PraxisBeadRole ? true : never;
const _roleCheckA: _AllPraxisRolesCovered = true;
void _roleCheckA;
const _roleCheckB: _NoExtraLocalRoles = true;
void _roleCheckB;

// ─── Per-type fields ─────────────────────────────────────────────────

export interface InboxFields {
  readonly kind: "inbox";
  readonly title: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface NextActionFields {
  readonly kind: "next-action";
  readonly title: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface ProjectFields {
  readonly kind: "project";
  readonly title: string;
  readonly outcome?: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface WaitingForFields {
  readonly kind: "waiting-for";
  readonly title: string;
  readonly waiter: string;
  readonly expectedAt?: number;
  readonly description?: string;
  readonly notes?: string;
}

export interface SomedayFields {
  readonly kind: "someday";
  readonly title: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface ReferenceFields {
  readonly kind: "reference";
  readonly title: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface DecisionFields {
  readonly kind: "decision";
  readonly title: string;
  readonly decidedAt: number;
  readonly supersededBy?: string;
  readonly description?: string;
  readonly notes?: string;
}

export interface CommitmentFields {
  readonly kind: "commitment";
  readonly title: string;
  readonly committedTo: string;
  readonly due: number;
  readonly description?: string;
  readonly notes?: string;
}

export interface OutputFields {
  readonly kind: "output";
  readonly title: string;
  readonly specId: string;
  readonly ratifiedAt?: number;
  readonly description?: string;
  readonly notes?: string;
}

export type BeadFields =
  | InboxFields
  | NextActionFields
  | ProjectFields
  | WaitingForFields
  | SomedayFields
  | ReferenceFields
  | DecisionFields
  | CommitmentFields
  | OutputFields;

// ─── Sub-blocks ──────────────────────────────────────────────────────

export interface BeadMetadata {
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly createdBy?: string;
  readonly version: number;
  readonly parentId?: string;
  readonly childOrderHints?: ReadonlyArray<string>;
  readonly specPath?: string;
  readonly assignee?: string;
  readonly instanceKey?: string;
  readonly factoryId?: string;
  // Set producer-side by `spec activate-execution` (praxis emitter maps
  // `bead.metadata.execution_id`); the projection consumer materializes it
  // into `beads.metadata.executionId` (schema.ts). Its presence is the
  // belt-and-braces execution-surface signal (bead-surfaces.ts, §2.4).
  readonly executionId?: string;
}

export interface BeadVisibility {
  readonly tickleUntil?: number;
}

// `BeadValueObject` mirrors the Convex row minus Convex-flavoured fields
// (`userId`, `_id`, `_creationTime`). Cross-row references are plain
// strings here — the domain MUST NOT import `Id<...>` from convex.
// `writer`/`role`/`validationFingerprint` are optional v.string()-shipped
// fields (existing rows pre-date them); use `getBeadWriter` to read the
// effective writer with the legacy default applied.
export interface BeadValueObject {
  readonly praxisId: string;
  readonly shortId: string;
  readonly type: BeadType;
  readonly state: BeadState;
  readonly priority: number;
  readonly schemaVersion: string;
  readonly metadata: BeadMetadata;
  readonly visibility: BeadVisibility;
  readonly fields: BeadFields;
  readonly extensions?: Readonly<Record<string, unknown>>;
  readonly writer?: BeadWriter;
  readonly role?: BeadRole;
  readonly validationFingerprint?: string;
}

// ─── State validity ──────────────────────────────────────────────────

const STATES_BY_TYPE: Readonly<Record<BeadType, ReadonlyArray<BeadState>>> = {
  inbox: ["inbox"],
  "next-action": ["clarified", "active", "done", "archived"],
  project: ["clarified", "active", "done", "archived"],
  "waiting-for": ["waiting", "clarified", "done", "archived"],
  someday: ["dormant", "archived"],
  reference: ["dormant", "archived"],
  decision: ["clarified", "archived"],
  commitment: ["clarified", "active", "done", "archived"],
  output: ["clarified", "done", "archived"],
};

export function isBeadStateAllowed(input: {
  type: BeadType;
  state: BeadState;
}): boolean {
  const { type, state } = input;
  return STATES_BY_TYPE[type].includes(state);
}

// ─── Completion (success-terminal state per type) ────────────────────
// The state at which a bead of each type is considered *successfully*
// complete. Most types end at "done"; `decision` beads have no "done"
// state — their lifecycle terminates at "clarified" (the decision has
// been made), per STATES_BY_TYPE above. `output` settles at "done".
// Open-ended types (inbox/someday/reference) are never "complete" → null.
// "archived" is terminal-but-discarded, never "complete". Used by the
// execution-overlay graph so a terminal decision bead reads as done
// (emerald) rather than rendering like an un-started action bead.
const COMPLETE_STATE_BY_TYPE: Readonly<Record<BeadType, BeadState | null>> = {
  inbox: null,
  "next-action": "done",
  project: "done",
  "waiting-for": "done",
  someday: null,
  reference: null,
  decision: "clarified",
  commitment: "done",
  output: "done",
};

export function isBeadComplete(input: {
  type: BeadType;
  state: BeadState;
}): boolean {
  return COMPLETE_STATE_BY_TYPE[input.type] === input.state;
}

// ─── Type guard ──────────────────────────────────────────────────────

export function isBeadOfType<T extends BeadType>(input: {
  bead: BeadValueObject;
  type: T;
}): boolean {
  const { bead, type } = input;
  return bead.type === type;
}

// ─── Grouping + filtering ────────────────────────────────────────────

export function groupBeadsByState(input: {
  beads: ReadonlyArray<BeadValueObject>;
}): Record<BeadState, BeadValueObject[]> {
  const { beads } = input;
  const result = BEAD_STATES.reduce(
    (acc, state) => {
      acc[state] = [];
      return acc;
    },
    {} as Record<BeadState, BeadValueObject[]>,
  );
  for (const bead of beads) {
    result[bead.state].push(bead);
  }
  return result;
}

/**
 * Thread-scoping label convention for conversation reference beads
 * (rnk-bqx8): the agent tags reference material created for a chat
 * thread with `thread:<threadId>` via the create verb's `-l` flag.
 * Single source for the format — the Reference-tab filter and the
 * TOOLS.md teaching prose both derive from this helper.
 */
export function threadLabel(threadId: string): string {
  return `thread:${threadId}`;
}

export function filterBeadsByTypes(input: {
  beads: ReadonlyArray<BeadValueObject>;
  types: ReadonlyArray<BeadType>;
}): BeadValueObject[] {
  const { beads, types } = input;
  if (types.length === 0) return [...beads];
  const allowed = new Set<BeadType>(types);
  return beads.filter((bead) => allowed.has(bead.type));
}

export function selectBeadByPraxisId(input: {
  beads: ReadonlyArray<BeadValueObject>;
  praxisId: string;
}): BeadValueObject | null {
  const { beads, praxisId } = input;
  return beads.find((bead) => bead.praxisId === praxisId) ?? null;
}

// ─── Writer accessors (two-writer rule) ──────────────────────────────

/**
 * Reads `bead.writer` with the legacy default applied. Pre-spec rows
 * (written before `beads.writer` existed) report as `"workspace"` —
 * matching the only writer that could have created them.
 */
export function getBeadWriter(bead: BeadValueObject): BeadWriter {
  return bead.writer ?? "workspace";
}

/**
 * Filters beads by their effective writer (applies the legacy default).
 * UI panels that want only validation-source beads call this with
 * `writer: "validation"`; workspace timelines call it with `"workspace"`.
 */
export function filterBeadsByWriter(input: {
  beads: ReadonlyArray<BeadValueObject>;
  writer: BeadWriter;
}): BeadValueObject[] {
  const { beads, writer } = input;
  return beads.filter((bead) => getBeadWriter(bead) === writer);
}

// ─── Role accessors ──────────────────────────────────────────────────

/**
 * Reads `bead.role` literally — no synthetic default. Absence is
 * meaningful: a bead without a role is a default workspace bead, NOT a
 * `prior_work` or `execution` bead. Symmetric with `getBeadWriter` in
 * shape but deliberately divergent in semantics (writer has a legacy
 * default; role does not — every role-tagged bead was tagged producer-
 * side, never by inference).
 */
export function getBeadRole(bead: BeadValueObject): BeadRole | undefined {
  return bead.role;
}

/**
 * Filters beads by role. UI panels that want only execution-runtime
 * beads call this with `role: "execution"`; validation-prior-work
 * panels call it with `role: "prior_work"`. Matches `getBeadRole`'s
 * no-default semantics: a bead with `role === undefined` matches
 * nothing.
 */
export function filterBeadsByRole(input: {
  beads: ReadonlyArray<BeadValueObject>;
  role: BeadRole;
}): BeadValueObject[] {
  const { beads, role } = input;
  return beads.filter((bead) => getBeadRole(bead) === role);
}

// ─── Display constants ───────────────────────────────────────────────

export const BEAD_STATE_DISPLAY_ORDER: ReadonlyArray<BeadState> = [
  "inbox",
  "clarified",
  "active",
  "waiting",
  "dormant",
  "done",
  "archived",
];

export const BEAD_TYPE_DISPLAY_LABEL: Readonly<Record<BeadType, string>> = {
  inbox: "Inbox",
  "next-action": "Next action",
  project: "Project",
  "waiting-for": "Waiting for",
  someday: "Someday",
  reference: "Reference",
  decision: "Decision",
  commitment: "Commitment",
  output: "Output",
};

export const BEAD_STATE_DISPLAY_LABEL: Readonly<Record<BeadState, string>> = {
  inbox: "Inbox",
  clarified: "Clarified",
  active: "Active",
  waiting: "Waiting",
  dormant: "Dormant",
  done: "Done",
  archived: "Archived",
};

// Compile-time completeness check: every BeadType (imported from praxis)
// MUST have a display-label entry. If praxis adds a new bead type and
// this file isn't updated, tsc fails here at the next clawcraft build.
const _displayLabelCompleteness: Record<BeadType, string> =
  BEAD_TYPE_DISPLAY_LABEL;
void _displayLabelCompleteness;
